import React from "react";
import { Route, Routes as RouterRoutes, Navigate, Outlet } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import AnonymousLayout from "../layouts/AnonymousLayout";
import Overview from "../pages/Dashboard/Overview";
import Events from "../pages/Dashboard/Events";
import Member from "../pages/member/Member";
import CreateMember from "../pages/member/CreateMember";
import EditMember from "../pages/member/EditMember";
import PageNotFound from "../pages/PageNotFound";

import StudentDistribution from "../pages/AiAnalytics/StudentDistribution";
import Hyzine from "../pages/AiAnalytics/Hyzine";
import CameraStatus from "../pages/AiAnalytics/CameraStatus";
import Settings from "../pages/settings/Settings";
import SafetyReports from "../pages/settings/SafetyReports";
import CloudflareApplicationFrame from "../pages/cloudflare/CloudflareApplicationFrame";
import AppLoginPage from "../pages/auth/AppLogin";
import ForgotPasswordPage from "../pages/auth/ForgotPassword";
import ResetPasswordPage from "../pages/auth/ResetPassword";
import SetPasswordPage from "../pages/auth/SetPassword";
import AuthCallback from "../pages/auth/AuthCallback";
import ApplicationNotAssigned from "../pages/auth/ApplicationNotAssigned";
import { RequireAssignedApplication } from "../guards/RequireAssignedApplication";
import { getAuthCallbackPath } from "../auth/session";
import { APPLICATION_NOT_ASSIGNED_PATH } from "../auth/session/authSession.service";
import { hasAppLoggedOutFlag, redirectToKeycloakLogin, redirectToSsoLogin } from "../auth/logout";
import { isAppSessionActive } from "../auth/session/authGate";
import { isPostLoginGraceActive } from "../auth/session/authSession.grace";
import { AuthTransitionScreen } from "../Components/AuthTransitionScreen";

// ─── Shared Dashboard Package ─────────────────────────────────────────────────
import {
  CmmZone,
  CmmPTZ,
  Cameras,
  CreateCamera,
  Locations,
  GpuDetails,
  NvrLive,
  NvrRecording,
  NvrClip,
  VideoAgent,
} from "@variphigen/shared-dashboards";
import Forbidden from "../pages/Forbidden";
import { FeatureRouteGuard } from "../guards/FeatureRouteGuard";
import { EVENTS_PAGE_PATH } from "./paths";

const authCallbackPath = getAuthCallbackPath();


const isViewerUser = (): boolean => {
  try {
    const authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
    const roles = Array.isArray(authUser?.roles) ? authUser.roles : [];
    return roles.some((r: unknown) => String(r).toUpperCase() === "VIEWER");
  } catch {
    return false;
  }
};

const MemberRouteGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  return isViewerUser() ? <Navigate to="/" replace /> : children;
};

function LoggedOutRedirect() {
  React.useEffect(() => {
    void redirectToKeycloakLogin();
  }, []);
  return <AuthTransitionScreen />;
}

function SsoLoginRedirect() {
  React.useEffect(() => {
    void redirectToSsoLogin();
  }, []);
  return <AuthTransitionScreen />;
}

function RequireAuth() {
  if (hasAppLoggedOutFlag()) {
    return <LoggedOutRedirect />;
  }

  if (isPostLoginGraceActive()) {
    return <Outlet />;
  }

  if (!isAppSessionActive()) {
    return <SsoLoginRedirect />;
  }

  return <Outlet />;
}

export const AppRoutes: React.FC = () => {
  return (
    <RouterRoutes>
      <Route path={authCallbackPath} element={<AuthCallback />} />
      <Route path={APPLICATION_NOT_ASSIGNED_PATH} element={<ApplicationNotAssigned />} />

      <Route element={<AnonymousLayout />}>
        <Route path="/login" element={<AppLoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/set-password" element={<SetPasswordPage />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<RequireAssignedApplication />}>
          <Route element={<MainLayout />}>
        <Route path="/" element={<Overview />} />
        <Route path="/overview" element={<Navigate to="/" replace />} />
        <Route path={EVENTS_PAGE_PATH} element={<Events />} />
        <Route path="/events" element={<Navigate to={EVENTS_PAGE_PATH} replace />} />
        <Route
          path="/member"
          element={
            <MemberRouteGuard>
              <Member />
            </MemberRouteGuard>
          }
        />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/safety-reports" element={<SafetyReports />} />
        <Route
          path="/member/create"
          element={
            <MemberRouteGuard>
              <CreateMember />
            </MemberRouteGuard>
          }
        />
        <Route
          path="/member/edit/:id"
          element={
            <MemberRouteGuard>
              <EditMember />
            </MemberRouteGuard>
          }
        />
        {/* <Route path="/profile" element={<Profile />} /> */}
        <Route path="/cmm/zone" element={<FeatureRouteGuard module="cmm" feature="configure-roi"><CmmZone /></FeatureRouteGuard>} />
        <Route path="/cmm/ptz" element={<FeatureRouteGuard module="cmm" feature="ptz-control"><CmmPTZ /></FeatureRouteGuard>} />
        <Route path="/cmm/camera" element={<FeatureRouteGuard module="cmm" feature="camera"><Cameras /></FeatureRouteGuard>} />
        <Route path="/cmm/create-camera" element={<FeatureRouteGuard module="cmm" feature="camera"><CreateCamera /></FeatureRouteGuard>} />
        <Route path="/cmm/locations" element={<FeatureRouteGuard module="cmm" feature="locations"><Locations /></FeatureRouteGuard>} />
        <Route path="/cmm/gpu" element={<FeatureRouteGuard module="cmm" feature="gpus"><GpuDetails /></FeatureRouteGuard>} />
        <Route path="/nvr-live" element={<FeatureRouteGuard module="vms" feature="live-streaming"><NvrLive /></FeatureRouteGuard>} />
        <Route path="/nvr-recording" element={<FeatureRouteGuard module="vms" feature="recordings"><NvrRecording /></FeatureRouteGuard>} />
        <Route path="/nvr-clip" element={<FeatureRouteGuard module="vms" feature="find-clip"><NvrClip /></FeatureRouteGuard>} />
        <Route path="/video-agent" element={<FeatureRouteGuard module="agent" feature="video"><VideoAgent /></FeatureRouteGuard>} />
        <Route path="/403" element={<Forbidden />} />
        <Route
          path="/ai-analytics/student-distribution"
          element={
            <FeatureRouteGuard module="ai-analytics" feature="distribution">
              <StudentDistribution />
            </FeatureRouteGuard>
          }
        />
        <Route
          path="/ai-analytics/hyzine"
          element={
            <FeatureRouteGuard module="ai-analytics" feature="hyzine">
              <Hyzine />
            </FeatureRouteGuard>
          }
        />

        <Route path="/ai-status" element={<CameraStatus />} />
        {/* Prefix avoids shadowing first-class routes like /video-agent (/:appEmbed would match them). */}
        <Route path="/embed/:appEmbed" element={<CloudflareApplicationFrame />} />
          </Route>
        </Route>
      </Route>
    </RouterRoutes>
  );
};

